// Motion pieces for the climb on goals/progress (goal_trail_controller):
// timing helpers, the coins that fly in and out, and the short-lived particles
// (sparks, dust, glints, confetti) a landing or a banked camp throws off.
//
// Everything here animates transform and opacity only, removes its own nodes
// when it finishes, and does nothing under prefers-reduced-motion.

export const SPRING = "cubic-bezier(.34,1.56,.64,1)"
export const EASE_IN_OUT = "cubic-bezier(.65,0,.35,1)"
export const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
export const easeOutQuad = (t) => 1 - Math.pow(1 - t, 2)

export function reducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, reducedMotion() ? 0 : ms))
}

// Web Animations with a promise that never rejects: a cancelled animation
// (the page leaving mid-flight) just settles.
export function anim(el, frames, options) {
  if (reducedMotion() || !el) return Promise.resolve()
  return el.animate(frames, { fill: "none", ...options }).finished.catch(() => {})
}

// A frame-by-frame tween for motion Web Animations can't express (a path the
// walker follows). `fn` gets the eased progress 0..1.
export function tween(ms, fn, ease = easeInOut) {
  if (reducedMotion()) {
    fn(1)
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    const t0 = performance.now()
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms)
      fn(ease(p))
      if (p < 1) requestAnimationFrame(step)
      else resolve()
    }
    requestAnimationFrame(step)
  })
}

// One coin: pops out of where it starts, winds up (anticipation), then flies
// an arc to `to`, spinning and shrinking as it lands. Screen coordinates.
export function coinFly(k, from, to, { ms = 860, windup = true, delay = k * 90, spin = 540, lift = 80, className = "gp-coin" } = {}) {
  if (reducedMotion()) return Promise.resolve()
  const coin = document.createElement("span")
  coin.className = className
  document.body.appendChild(coin)
  const [x0, y0] = [from[0] - 11, from[1] - 11]
  const [x1, y1] = [to[0] - 11, to[1] - 11]
  const frames = []
  const flyFrom = windup ? 0.3 : 0
  if (windup) {
    frames.push(
      { offset: 0, transform: `translate(${x0}px,${y0}px) scale(.2)`, opacity: 0 },
      { offset: 0.12, transform: `translate(${x0}px,${y0 - 14}px) scale(1.1)`, opacity: 1 },
      { offset: 0.26, transform: `translate(${x0}px,${y0 + 3}px) scale(1.18,.8)`, opacity: 1 }
    )
  }
  for (let j = 0; j <= 18; j++) {
    const t = j / 18
    const e = easeInOut(t)
    const s = Math.sin(Math.PI * t)
    frames.push({
      offset: flyFrom + (1 - flyFrom) * t,
      transform: `translate(${x0 + (x1 - x0) * e + s * 22}px,${y0 + (y1 - y0) * e - (lift + k * 6) * s}px) scale(${1 + 0.15 * s - 0.45 * t * t}) rotate(${t * spin}deg)`,
      opacity: t > 0.95 ? 0 : 1
    })
  }
  return coin.animate(frames, { duration: ms, delay, easing: "linear", fill: "backwards" })
    .finished.then(() => coin.remove(), () => coin.remove())
}

// A particle placed in scene percentages inside `host`, animated, then removed.
function particle(host, className, [left, top], frames, options) {
  if (reducedMotion()) return
  const el = document.createElement("span")
  el.className = className
  el.style.left = `${left}%`
  el.style.top = `${top}%`
  host.appendChild(el)
  el.animate(frames, options).finished.then(() => el.remove(), () => el.remove())
  return el
}

// A sparkle off the feet mid-jump, or a puff of dust when dragged back down.
export function trailBit(host, at, kind) {
  const drift = (Math.random() - 0.5) * 10
  const frames = kind === "spark"
    ? [{ transform: "translate(-50%,-50%) scale(1) rotate(0)", opacity: 1 }, { transform: `translate(calc(-50% + ${drift}px), calc(-50% + 6px)) scale(.2) rotate(90deg)`, opacity: 0 }]
    : [{ transform: "translate(-50%,-50%) scale(.6)", opacity: 0.7 }, { transform: `translate(calc(-50% + ${drift}px), calc(-50% - 8px)) scale(1.6)`, opacity: 0 }]
  particle(host, `gp-trail is-${kind}`, at, frames, { duration: kind === "spark" ? 520 : 600, easing: "ease-out" })
}

// Dust kicked out sideways from under the feet on landing.
export function landDust(host, [left, top]) {
  [-1, 1, -1, 1].forEach((side, k) => {
    const dx = side * (16 + k * 6)
    particle(host, "gp-trail is-dust", [left, top], [
      { transform: "translate(-50%,-50%) scale(.5)", opacity: 0.8 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% - ${4 + k * 2}px)) scale(1.5)`, opacity: 0 }
    ], { duration: 520, easing: "cubic-bezier(.2,.8,.3,1)" })
  })
}

// A fan of gold and green sparks off a camp that just turned to gold.
export function burst(host, at, n = 14) {
  const colours = ["#F5B301", "#FFE58A", "#34D399", "#fff"]
  for (let k = 0; k < n; k++) {
    const a = -Math.PI * (k / (n - 1))
    const d = 30 + Math.random() * 26
    const el = particle(host, "gp-spark", at, [
      { transform: "translate(-50%,-50%) scale(1)", opacity: 1 },
      { transform: `translate(calc(-50% + ${Math.cos(a) * d}px), calc(-50% + ${Math.sin(a) * d}px)) scale(.3)`, opacity: 0 }
    ], { duration: 650, easing: "cubic-bezier(.2,.8,.3,1)" })
    if (el) el.style.background = colours[k % colours.length]
  }
}

// Four star glints around a point; `offsets` are in scene percentages.
export function glints(host, [left, top], offsets) {
  offsets.forEach(([dx, dy], k) => {
    particle(host, "gp-glint", [left + dx, top + dy], [
      { transform: "translate(-50%,-50%) scale(0) rotate(0)", opacity: 1 },
      { transform: "translate(-50%,-50%) scale(1.3) rotate(45deg)", opacity: 1, offset: 0.45 },
      { transform: "translate(-50%,-50%) scale(0) rotate(90deg)", opacity: 0 }
    ], { duration: 640, delay: 80 + k * 70, easing: "ease-out", fill: "backwards" })
  })
}

// Coins jumping out of the opened chest.
export function goldBits(host, at, n = 7) {
  for (let k = 0; k < n; k++) {
    const dx = (k - (n - 1) / 2) * 13 + Math.random() * 6
    const h = 34 + Math.random() * 26
    const frames = []
    for (let j = 0; j <= 12; j++) {
      const t = j / 12
      frames.push({
        transform: `translate(calc(-50% + ${dx * t}px), calc(-50% + ${-h * Math.sin(Math.PI * t) + 30 * t}px)) rotate(${t * 360}deg)`,
        opacity: t > 0.8 ? (1 - t) * 5 : 1
      })
    }
    particle(host, "gp-gold-bit", at, frames, { duration: 700, delay: k * 30, easing: "linear", fill: "backwards" })
  }
}

// The summit: confetti falls over the whole screen.
export function confetti() {
  if (reducedMotion()) return
  const colours = ["#3D8AFE", "#F5B301", "#34D399", "#F472B6", "#fff"]
  for (let k = 0; k < 70; k++) {
    const el = document.createElement("span")
    el.className = "gp-confetti"
    el.style.background = colours[k % colours.length]
    el.style.left = `${Math.random() * 100}vw`
    el.style.top = "-20px"
    document.body.appendChild(el)
    el.animate(
      [{ transform: "translateY(0) rotate(0)" }, { transform: `translate(${Math.random() * 80 - 40}px, ${window.innerHeight + 40}px) rotate(${Math.random() * 720}deg)` }],
      { duration: 1800 + Math.random() * 1400, delay: Math.random() * 400, easing: "cubic-bezier(.25,.6,.4,1)" }
    ).finished.then(() => el.remove(), () => el.remove())
  }
}

// A label that pops up over a point, holds, then floats away: "+$335" or
// "$2,000 camp!". className picks the look (gp-plus, gp-pop, gp-pop is-back).
export async function floatLabel(host, className, [left, top], text, holdMs = 650) {
  if (reducedMotion()) return
  const el = document.createElement("span")
  el.className = className
  el.textContent = text
  el.style.left = `${left}%`
  el.style.top = `${top}%`
  host.appendChild(el)
  await anim(el, [
    { transform: "translate(-50%,-50%) scale(.35)", opacity: 0 },
    { transform: "translate(-50%,-100%) scale(1.13)", opacity: 1, offset: 0.52 },
    { transform: "translate(-50%,-100%) scale(1)", opacity: 1 }
  ], { duration: 450, easing: "ease-out", fill: "forwards" })
  await wait(holdMs)
  await anim(el, [{ transform: "translate(-50%,-100%)", opacity: 1 }, { transform: "translate(-50%,-165%)", opacity: 0 }],
    { duration: 400, easing: "ease-in", fill: "forwards" })
  el.remove()
}
